import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronUp, RefreshCw, Search, Grid, LayoutList,
  Monitor, FileUp, FolderUp, Regex, CaseSensitive, WholeWord, Activity, ExternalLink, X, Globe, Database, Loader2,
  Zap, HardDrive, LayoutDashboard
} from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../../types';
import { detectSeparator, normalizePath } from '../../utils/pathUtils';
import { getRootLabel, normalizeRootInput, t } from '../../i18n';

// Status indicator component for search engine initialization
const SearchEngineStatusIndicator: React.FC = () => {
  const { status, progress } = useFileSearchStatusStore();

  const getStatusText = () => {
    // If we have a message from backend, use it directly
    if (progress?.message) {
      return progress.message;
    }

    switch (status) {
      case FileSearchStatus.INDEXING:
        return t('searchEngineStatusScanning');
      case FileSearchStatus.BUILDING_INDEX:
        return t('searchEngineStatusBuilding');
      case FileSearchStatus.CALCULATING_SIZES:
        return t('searchEngineStatusCalculating');
      default:
        return t('searchEngineStatusInitializing');
    }
  };

  // Get progress percentage (0-100)
  const progressPercent = progress?.progress ?? 0;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-yellow-500/30 rounded-full min-w-[160px]">
      <Loader2 size={14} className="text-yellow-400 animate-spin flex-shrink-0" />
      <div className="flex-1 flex flex-col gap-0.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-300">{getStatusText()}</span>
          <span className="text-xs text-yellow-400 font-medium ml-2">{progressPercent}%</span>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-400 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Init timing 简略摘要: ready 状态下显示总耗时 + 策略图标, 点击展开详情面板
// X 按钮可临时关闭(隐藏到下次 init), 详情面板仍可通过其他入口打开 (如果需要)
const InitTimingSummary: React.FC = () => {
  // Subscribe field-by-field so high-frequency store updates (e.g. `progress`)
  // don't drag this pill into needless re-renders.
  const initTiming = useFileSearchStatusStore((s) => s.initTiming);
  const isInitTimingSummaryHidden = useFileSearchStatusStore((s) => s.isInitTimingSummaryHidden);
  const openInitTimingPanel = useFileSearchStatusStore((s) => s.openInitTimingPanel);
  const dismissInitTimingSummary = useFileSearchStatusStore((s) => s.dismissInitTimingSummary);
  if (!initTiming || !initTiming.valid) return null;
  if (isInitTimingSummaryHidden) return null;

  const totalSec = (initTiming.total_ms / 1000).toFixed(1);
  let icon = <Zap size={12} className="text-green-400" />;
  let label = '从缓存加载';
  if (initTiming.strategy === 'all_full_scan') {
    icon = <HardDrive size={12} className="text-amber-400" />;
    label = '全盘扫描';
  } else if (initTiming.strategy === 'mixed') {
    icon = <Zap size={12} className="text-blue-400" />;
    label = '混合';
  } else if (initTiming.strategy === 'none') {
    return null;
  }

  return (
    <div className="flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-win-accent/50 transition-all">
      <button
        onClick={openInitTimingPanel}
        title="点击查看初始化耗时详情"
        className="flex items-center gap-1.5 cursor-pointer"
      >
        {icon}
        <span className="text-xs text-slate-400 tabular-nums">{totalSec}s</span>
        <span className="text-xs text-slate-500">·</span>
        <span className="text-xs text-slate-400">{label}</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); dismissInitTimingSummary(); }}
        title="关闭此提示"
        className="ml-1 p-0.5 rounded-full hover:bg-white/10 text-slate-500 hover:text-slate-300 transition-colors"
      >
        <X size={11} />
      </button>
    </div>
  );
};

interface CommandBarProps {
  onClose?: () => void;
  onOpenNewWindow?: () => void;
}

const CommandBar: React.FC<CommandBarProps> = ({ onClose, onOpenNewWindow }) => {
  const {
    currentPath, setCurrentPath, goUp, refresh, viewMode, setViewMode, searchQuery, setSearchQuery,
    searchSettings, setSearchSettings,
    goBack, goForward, history, historyIndex, fs, uploadFiles, toggleUploadManager, uploadTasks,
    isUploadManagerOpen, openGlobalSearch, uploadTrigger, clearUploadTrigger,
    openSearchEngineInitModal
  } = useFileManagerStore();
  const { status: searchEngineStatus } = useFileSearchStatusStore();
  const isSearchEngineReady = searchEngineStatus === FileSearchStatus.READY;

  const [isEditingPath, setIsEditingPath] = useState(false);
  const [tempPath, setTempPath] = useState(currentPath);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetPathRef = useRef<string | null>(null);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < history.length - 1;
  const isUploading = uploadTasks.some(t => t.status === 'uploading');
  const rootLabel = getRootLabel();

  // Auto-select text when entering edit mode
  useEffect(() => {
    if (isEditingPath && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditingPath]);

  // Sync tempPath when currentPath changes externally
  useEffect(() => {
    if (!isEditingPath) {
      setTempPath(currentPath === 'root' ? rootLabel : currentPath);
    }
  }, [currentPath, isEditingPath, rootLabel]);

  // Handle upload trigger from context menu
  useEffect(() => {
    if (uploadTrigger) {
      uploadTargetPathRef.current = uploadTrigger.targetPath;
      if (uploadTrigger.type === 'file') {
        fileInputRef.current?.click();
      } else if (uploadTrigger.type === 'folder') {
        folderInputRef.current?.click();
      }
      clearUploadTrigger();
    }
  }, [uploadTrigger, clearUploadTrigger]);

  const handlePathSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = normalizeRootInput(tempPath);
      setCurrentPath(target);
      setIsEditingPath(false);
    } else if (e.key === 'Escape') {
      setIsEditingPath(false);
      setTempPath(currentPath === 'root' ? rootLabel : currentPath);
    }
  };

  const buildBreadcrumbs = (path: string) => {
    const sep = detectSeparator(path);
    const normalized = normalizePath(path);
    const parts = normalized.split(sep).filter(Boolean);
    const breadcrumbs: { label: string; path: string }[] = [];
    if (parts.length === 0) return breadcrumbs;

    if (/^[A-Za-z]:$/.test(parts[0])) {
      let current = parts[0] + sep;
      breadcrumbs.push({ label: parts[0], path: current });
      for (let i = 1; i < parts.length; i++) {
        current = current.endsWith(sep) ? current + parts[i] : `${current}${sep}${parts[i]}`;
        breadcrumbs.push({ label: parts[i], path: current });
      }
      return breadcrumbs;
    }

    let current = '';
    for (const part of parts) {
      current = current ? `${current}${sep}${part}` : part;
      breadcrumbs.push({ label: part, path: current });
    }
    return breadcrumbs;
  };
  const breadcrumbs = currentPath === 'root' ? [] : buildBreadcrumbs(currentPath);
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const targetPath = uploadTargetPathRef.current || undefined;
      await uploadFiles(files.map(f => ({ file: f })), targetPath);
      e.target.value = '';
      uploadTargetPathRef.current = null;
    }
  };

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
           const files = Array.from(e.target.files);
           const targetPath = uploadTargetPathRef.current || undefined;
           await uploadFiles(files.map(f => ({
               file: f,
               // @ts-ignore
               relativePath: f.webkitRelativePath || f.name
           })), targetPath);
           e.target.value = '';
           uploadTargetPathRef.current = null;
      }
  };

  return (
    <div className="h-12 border-b border-white/5 flex items-center px-4 justify-between bg-black/10">
      <div className="flex items-center space-x-2 flex-1 min-w-0 mr-4">
        <div className="flex items-center space-x-1 mr-2 flex-shrink-0">
          <button 
            onClick={() => setCurrentPath('root')}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            title={rootLabel}
          >
            <Monitor size={18} />
          </button>
          <button 
            onClick={goBack} 
            disabled={!canGoBack}
            className="p-1.5 hover:bg-white/10 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button 
            onClick={goForward} 
            disabled={!canGoForward}
            className="p-1.5 hover:bg-white/10 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <button onClick={goUp} className="p-1.5 hover:bg-white/10 rounded transition-colors"><ChevronUp size={18} /></button>
          <button onClick={refresh} className="p-1.5 hover:bg-white/10 rounded transition-colors"><RefreshCw size={16} /></button>
        </div>

        {/* Address Bar Area */}
        <div 
          className="flex-1 bg-white/5 border border-white/10 rounded px-3 h-8 text-sm flex items-center max-w-2xl cursor-text transition-colors hover:border-white/20 relative overflow-hidden"
          onDoubleClick={() => {
              setTempPath(currentPath === 'root' ? rootLabel : currentPath);
              setIsEditingPath(true);
          }}
        >
          {isEditingPath ? (
             <input
               ref={inputRef}
               type="text"
               value={tempPath}
               onChange={(e) => setTempPath(e.target.value)}
               onBlur={() => {
                   setIsEditingPath(false);
                   setTempPath(currentPath === 'root' ? rootLabel : currentPath);
               }}
               onKeyDown={handlePathSubmit}
               className="w-full bg-transparent outline-none text-slate-200"
               autoFocus
             />
          ) : (
             <div className="flex items-center w-full h-full overflow-hidden truncate">
                {currentPath === 'root' ? (
                    <span 
                        className="hover:underline cursor-pointer px-1 rounded hover:bg-white/5"
                        onClick={() => setCurrentPath('root')}
                    >
                        {rootLabel}
                    </span>
                ) : (
                    breadcrumbs.map((crumb, idx) => (
                        <React.Fragment key={`${crumb.path}-${idx}`}>
                        <span 
                            onClick={(e) => { e.stopPropagation(); setCurrentPath(crumb.path); }}
                            className="hover:underline cursor-pointer px-1 rounded hover:bg-white/5 flex-shrink-0"
                        >
                            {crumb.label || rootLabel}
                        </span>
                        {idx < breadcrumbs.length - 1 && <span className="text-slate-500 mx-1 flex-shrink-0">/</span>}
                        </React.Fragment>
                    ))
                )}
             </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-4 flex-shrink-0">
        {/* Init timing summary: 紧贴搜索按钮左侧, 仅 ready 且有 report 时显示 */}
        {isSearchEngineReady && <InitTimingSummary />}

        {/* Global Search / Search Engine Init / Status */}
        {isSearchEngineReady ? (
          // Ready state: show search button
          <button
            onClick={openGlobalSearch}
            title={t('commandGlobalSearchTitle')}
            className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-win-accent/50 transition-all group"
          >
            <Globe size={14} className="text-win-accent" />
            <span className="text-sm text-slate-400 group-hover:text-slate-200">{t('commandSearchAllFiles')}</span>
          </button>
        ) : searchEngineStatus === FileSearchStatus.UNINITIALIZED ? (
          // Uninitialized: show init button
          <button
            onClick={() => openSearchEngineInitModal(false)}
            title={t('commandSearchEngineInitTitle')}
            className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-yellow-500/50 transition-all group"
          >
            <Database size={14} className="text-yellow-500" />
            <span className="text-sm text-slate-400 group-hover:text-slate-200">{t('commandSearchEngine')}</span>
          </button>
        ) : (
          // Initializing: show status indicator
          <SearchEngineStatusIndicator />
        )}

        {/* Local Search Bar with Options */}
        <div className="relative flex items-center bg-white/5 border border-white/10 rounded-full pl-3 pr-1 group focus-within:border-win-accent transition-all">
          <Search size={14} className="text-slate-400 group-focus-within:text-win-accent mr-2" />
          <input
            type="text"
            placeholder={t('commandFilter')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent py-1 text-sm w-24 focus:w-36 outline-none transition-all"
          />

          <div className="flex items-center space-x-0.5 ml-1 border-l border-white/10 pl-1 py-0.5">
              <button
                onClick={() => setSearchSettings({ regex: !searchSettings.regex })}
                title={t('commandRegex')}
                className={`p-1 rounded transition-colors ${searchSettings.regex ? 'bg-win-accent text-white' : 'text-slate-500 hover:bg-white/10 hover:text-slate-300'}`}
              >
                  <Regex size={14} />
              </button>
              <button
                onClick={() => setSearchSettings({ caseSensitive: !searchSettings.caseSensitive })}
                title={t('commandMatchCase')}
                className={`p-1 rounded transition-colors ${searchSettings.caseSensitive ? 'bg-win-accent text-white' : 'text-slate-500 hover:bg-white/10 hover:text-slate-300'}`}
              >
                  <CaseSensitive size={14} />
              </button>
              <button
                onClick={() => setSearchSettings({ wholeWord: !searchSettings.wholeWord })}
                title={t('commandMatchWholeWord')}
                className={`p-1 rounded transition-colors ${searchSettings.wholeWord ? 'bg-win-accent text-white' : 'text-slate-500 hover:bg-white/10 hover:text-slate-300'}`}
              >
                  <WholeWord size={14} />
              </button>
          </div>
        </div>

        {/* View Switches */}
        <div className="flex items-center bg-white/5 rounded p-0.5">
          <button 
            onClick={() => setViewMode('grid')}
            title={t('commandGridView')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5'}`}
          >
            <Grid size={16} />
          </button>
          <button
            onClick={() => setViewMode('details')}
            title={t('commandDetailsView')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'details' ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5'}`}
          >
            <LayoutList size={16} />
          </button>
          <button
            onClick={() => setViewMode('treemap')}
            title={t('commandTreemapView')}
            className={`p-1.5 rounded transition-colors ${viewMode === 'treemap' ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5'}`}
          >
            <LayoutDashboard size={16} />
          </button>
        </div>

        {/* Upload & Manager Buttons */}
        <div className="flex items-center bg-white/5 rounded p-0.5">
          <button 
            onClick={() => fileInputRef.current?.click()}
            title={t('commandUploadFile')}
            className="p-1.5 rounded hover:bg-white/5 transition-colors"
          >
            <FileUp size={16} />
          </button>
          <button 
            onClick={() => folderInputRef.current?.click()}
            title={t('commandUploadFolder')}
            className="p-1.5 rounded hover:bg-white/5 transition-colors"
          >
            <FolderUp size={16} />
          </button>
          <div className="w-px h-4 bg-white/10 mx-1" />
          <button 
            onClick={toggleUploadManager}
            title={t('commandUploadManager')}
            className={`p-1.5 rounded transition-colors ${isUploadManagerOpen ? 'bg-white/20 text-win-accent shadow-inner' : 'hover:bg-white/5'} ${isUploading ? 'text-win-accent' : ''}`}
          >
            <Activity size={16} className={isUploading ? 'animate-pulse' : ''} />
          </button>
        </div>

        {/* Window Controls */}
        {(onOpenNewWindow || onClose) && (
          <div className="flex items-center bg-white/5 rounded p-0.5">
            {onOpenNewWindow && (
              <button 
                onClick={onOpenNewWindow}
                title={t('commandOpenNewWindow')}
                className="p-1.5 rounded hover:bg-white/5 transition-colors"
              >
                <ExternalLink size={16} />
              </button>
            )}
            {onClose && (
              <button 
                onClick={onClose}
                title={t('commandClose')}
                className="p-1.5 rounded hover:bg-red-500/80 hover:text-white text-slate-400 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        {/* Hidden Inputs */}
        <input 
            ref={fileInputRef}
            type="file" 
            multiple 
            className="hidden" 
            onChange={handleFileUpload} 
        />
        <input 
            ref={folderInputRef}
            type="file" 
            // @ts-ignore
            webkitdirectory=""
            directory=""
            className="hidden" 
            onChange={handleFolderUpload} 
        />
      </div>
    </div>
  );
};

export default CommandBar;
